/**
 * Compact, bounded-height, scrollable teacher-switch modal — shared shell
 * for the idle re-lock (non-dismissible, see LOCATION_IDLE_MS) AND the ⇄
 * "switch teacher" chip (dismissible), with in-modal group re-sorting. 1:1 from the
 * golden mock (~/mockups/teacher-switch-modal.html); see
 * docs/guides/modal-design.md for the ratified bounded-height/scroll-fade
 * pattern this is the reference implementation of (Task 2.3+,
 * docs/plans/2026-07-30-location-login.md).
 *
 * Group-chip taps only change local `activeGroupId` — no commit, no audit
 * write, just a re-sort of the list below. A row tap commits via `onPick`
 * using the CHIP-selected group, not necessarily the teacher's own
 * group_id (the floater-covering-unexpectedly case).
 *
 * Full-device Sign out lives in the footer behind password confirm (same
 * SignOutConfirm as GroupPicker) so mid-session kiosks can eject without a
 * header button — and without a one-tap foot-gun.
 */

import { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog';
import { InitialsAvatar } from '@/components/ui/initials-avatar';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { TILE_EMOJI } from '../constants';
import { signOutLocation } from '../utils/signOutLocation';
import { SignOutConfirm } from './SignOutConfirm';
import type { LocationSessionGroup, LocationSessionTeacher, LocationTeacherToday } from '../types';

const CONFIRM_DELAY_MS = 550;

export interface RelockSheetProps {
  /** 'idle' = non-dismissible (LOCATION_IDLE_MS timeout); 'switch' = dismissible (⇄ chip). */
  variant: 'idle' | 'switch';
  groups: LocationSessionGroup[];
  teachers: LocationTeacherToday[];
  isLoading: boolean;
  initialGroupId: string;
  /** Uppercased location code for the "Also at {label} today" section label. */
  locationLabel: string;
  /**
   * The device's OWN location_code (lowercase, e.g. 'rb3') — any group in
   * `groups` whose `locationCode` differs gets a small sibling badge on its
   * chip (rb3↔rb5 campus: LocationGate passes own + sibling groups, and
   * still appends session.group if the fetch omitted it so `initialGroupId`
   * always resolves).
   */
  ownLocationCode: string;
  /**
   * The teacher relock() just cleared — when set on the 'idle' variant,
   * renders "Still {name}? Continue" as the primary action above the full
   * teacher list. Ignored on the 'switch' variant.
   */
  lastTeacher?: LocationSessionTeacher | null;
  onPick: (group: LocationSessionGroup, teacher: LocationSessionTeacher) => void | Promise<void>;
  /** Backdrop click / ESC — only ever invoked for variant 'switch'. */
  onDismiss?: () => void;
}

export function RelockSheet({
  variant,
  groups,
  teachers,
  isLoading,
  initialGroupId,
  locationLabel,
  ownLocationCode,
  lastTeacher,
  onPick,
  onDismiss,
}: RelockSheetProps) {
  const { signOut } = useAuth();
  const [activeGroupId, setActiveGroupId] = useState(initialGroupId);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [fadeHidden, setFadeHidden] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  async function handleSignOut(): Promise<void> {
    setIsSigningOut(true);
    try {
      await signOutLocation(signOut, ownLocationCode);
    } catch (err) {
      setIsSigningOut(false);
      // Re-throw so SignOutConfirm keeps the dialog open and surfaces the error.
      throw err instanceof Error ? err : new Error('Sign out failed — try again');
    }
  }

  const inGroup = teachers.filter((t) => t.groupIds.includes(activeGroupId));
  const rest = teachers.filter((t) => !t.groupIds.includes(activeGroupId));
  const currentLastTeacher = lastTeacher && inGroup.find((t) => t.staffId === lastTeacher.staffId);

  function handleContinue(): void {
    if (!currentLastTeacher || confirmingId) return;
    // Resolve BEFORE touching confirmingId: a miss here must never leave
    // the sheet's tap-guard (confirmingId set, never cleared) stuck forever
    // — that bricked the non-dismissible idle variant until reload (a
    // reload that then restored the exact same session and re-bricked it).
    const group = groups.find((g) => g.id === activeGroupId);
    if (!group) {
      toast.error('That group is no longer available — pick another group');
      return;
    }
    const teacher: LocationSessionTeacher = {
      staffId: currentLastTeacher.staffId,
      firstName: currentLastTeacher.firstName,
      fullName: currentLastTeacher.fullName,
    };
    setConfirmingId(teacher.staffId);
    void Promise.resolve(onPick(group, teacher)).catch((err: unknown) => {
      setConfirmingId(null);
      toast.error(err instanceof Error ? err.message : 'Failed to continue');
    });
  }

  const updateFade = () => {
    const el = scrollRef.current;
    if (!el) return;
    setFadeHidden(el.scrollHeight - el.scrollTop - el.clientHeight < 2);
  };

  // Recompute after the list re-sorts (chip tap) or loads — content height
  // changes without the user necessarily scrolling.
  useEffect(() => { updateFade(); }, [activeGroupId, teachers.length]);

  function handleRowPick(t: LocationTeacherToday) {
    if (confirmingId) return; // guard against a second tap while confirming
    // Same resolve-before-confirm ordering as handleContinue — see its
    // comment. Never throw: fail loudly via toast, keep the sheet usable.
    const group = groups.find((g) => g.id === activeGroupId);
    if (!group) {
      toast.error('That group is no longer available — pick another group');
      return;
    }
    setConfirmingId(t.staffId);
    const teacher: LocationSessionTeacher = { staffId: t.staffId, firstName: t.firstName, fullName: t.fullName };
    window.setTimeout(() => {
      // A rejected commit (e.g. the audit INSERT fails) must not leave the
      // ✓ confirm state stuck forever with the modal ignoring taps — clear
      // it and surface the error, keeping the modal open and usable.
      void Promise.resolve(onPick(group, teacher)).catch((err: unknown) => {
        setConfirmingId(null);
        toast.error(err instanceof Error ? err.message : 'Failed to switch teacher');
      });
    }, CONFIRM_DELAY_MS);
  }

  function handleOutsideDismiss(e: Event) {
    if (variant === 'idle') e.preventDefault();
  }

  function renderRow(t: LocationTeacherToday) {
    const confirming = confirmingId === t.staffId;
    return (
      <button
        key={t.staffId}
        type="button"
        onClick={() => handleRowPick(t)}
        className={cn(
          'flex w-full select-none items-center gap-3 rounded-2xl border-2 border-transparent px-2 py-2.5 text-left transition active:scale-[0.99]',
          confirming && 'border-primary/30 bg-primary/5',
        )}
      >
        <InitialsAvatar name={t.fullName} size={36} />
        <span className="flex-1 text-sm font-extrabold text-foreground">{t.firstName}</span>
        <span className={cn('text-sm font-black text-primary transition-opacity', confirming ? 'opacity-100' : 'opacity-0')}>
          ✓
        </span>
      </button>
    );
  }

  const title = variant === 'idle' ? "Who's there?" : 'Switch teacher';
  const subtitle =
    variant === 'idle'
      ? 'A while of inactivity — pick your name to keep going.'
      : 'Pick your name to continue.';

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onDismiss?.(); }}>
      <DialogPortal>
        <DialogOverlay className="bg-foreground/30" />
        <DialogPrimitive.Content
          onEscapeKeyDown={handleOutsideDismiss}
          onPointerDownOutside={handleOutsideDismiss}
          onInteractOutside={handleOutsideDismiss}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[72vh] w-full max-w-[420px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card shadow-2xl focus:outline-none"
        >
          <div className="flex-shrink-0 px-6 pb-3 pt-5 text-center">
            <div className="mb-1 text-2xl" aria-hidden="true">🔒</div>
            <DialogPrimitive.Title className="mb-1 text-lg font-extrabold text-primary">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-xs text-muted-foreground">{subtitle}</DialogPrimitive.Description>
            {variant === 'idle' && (
              <div className="mt-1.5 flex items-center justify-center gap-1 text-[11px] font-extrabold text-primary/80">
                🔒 locked until you pick — no dismiss
              </div>
            )}
          </div>

          {variant === 'idle' && currentLastTeacher && (
            <div className="flex-shrink-0 px-6 pb-3">
              <button
                type="button"
                onClick={handleContinue}
                disabled={confirmingId === currentLastTeacher.staffId}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-primary bg-gradient-to-br from-primary to-primary/80 px-4 py-2.5 text-sm font-extrabold text-primary-foreground shadow-md transition active:scale-[0.99] disabled:opacity-70"
              >
                Still {currentLastTeacher.firstName}? — Continue
              </button>
            </div>
          )}

          <div className="grid flex-shrink-0 grid-cols-4 gap-1.5 px-6 pb-3.5 pt-1">
            {groups.map((g, i) => {
              const isSibling = g.locationCode !== ownLocationCode;
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => {
                    setActiveGroupId(g.id);
                    // Golden mock: switching group lands the user back at
                    // "In this group", not wherever the previous list happened
                    // to be scrolled to.
                    if (scrollRef.current) scrollRef.current.scrollTop = 0;
                  }}
                  className={cn(
                    'flex items-center justify-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap rounded-full border-2 px-1.5 py-1.5 text-[11.5px] font-extrabold transition active:scale-95',
                    g.id === activeGroupId
                      ? 'border-primary bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-md'
                      : 'border-border bg-card text-muted-foreground',
                  )}
                >
                  {g.iconUrl ? (
                    <img src={g.iconUrl} alt="" loading="lazy" className="h-4 w-4 shrink-0 rounded-full object-cover" />
                  ) : (
                    <span aria-hidden="true">{TILE_EMOJI[i % TILE_EMOJI.length]}</span>
                  )}
                  <span>{g.name}</span>
                  {isSibling && (
                    <span className="shrink-0 rounded-full bg-primary-foreground/20 px-1 text-[8px] font-black leading-[13px]">
                      {g.locationCode.toUpperCase()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="relative flex min-h-0 flex-1 flex-col">
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-5 bg-gradient-to-b from-card to-transparent" />
            <div
              ref={scrollRef}
              data-testid="relock-scroll"
              onScroll={updateFade}
              className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain px-6 pb-4 [-webkit-overflow-scrolling:touch]"
            >
              {isLoading ? (
                <p className="p-4 text-center text-sm text-muted-foreground">Loading…</p>
              ) : (
                <>
                  <p className="mb-1.5 mt-1 pl-0.5 text-[10.5px] font-black uppercase tracking-wider text-muted-foreground/70">
                    In this group
                  </p>
                  {inGroup.map(renderRow)}
                  <p className="mb-1.5 mt-3 pl-0.5 text-[10.5px] font-black uppercase tracking-wider text-muted-foreground/70">
                    Also at {locationLabel} today
                  </p>
                  {rest.map(renderRow)}
                </>
              )}
            </div>
            <div
              data-testid="relock-fade-bottom"
              className={cn(
                'pointer-events-none absolute inset-x-0 bottom-0 z-10 h-5 bg-gradient-to-t from-card to-transparent transition-opacity',
                fadeHidden ? 'opacity-0' : 'opacity-100',
              )}
            />
          </div>

          {/* Sticky footer — outside the scroll pane so device exit is never
            * below-the-fold on a long teacher list (kiosk discoverability). */}
          <div className="sticky bottom-0 flex-shrink-0 border-t border-border bg-card px-6 py-3 text-center">
            <p className="flex items-center justify-center gap-1 text-[11px] font-bold text-muted-foreground">
              🛡️ Every switch is logged
            </p>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={isSigningOut}
              className="mt-2 text-xs font-bold text-primary underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50"
            >
              Sign out of this device
            </button>
          </div>

          <SignOutConfirm
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            onConfirm={handleSignOut}
            isSigningOut={isSigningOut}
          />
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
